import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { Typography } from "@tento-ui/ui/typography";
// A VALUE import, not `import type`: it is what registers `std-stepper`, and a
// type-only import would leave the template's element inert.
import { Stepper } from "@tento-ui/components/stepper";

// The marketing step list: the pricing page's "how it works" column.
//
// This component is now a REGISTER, not a layout. The marker column, the rail
// that chains it and the indent that clears it come from std-stepper; what is
// left here is the marketing tuning — a bigger circle on a filled ground, a
// louder number, and the measure the section reads at. The 28px badge and the
// 2px rail it used to draw itself were a third copy of an arithmetic that
// already existed twice, and the copy had the rail's position hardcoded
// (`left: 14px; top: 28px`), so moving the badge size would have left the rail
// behind.
@component({ name: "lona-steps" })
export class StepList extends LonaWebComponent {
  private $steps: Stepper = this.hydrate("steps");

  static makeWith(props: StepList.Props): StepList {
    return (StepList.make() as StepList).bind(props);
  }

  bind(props: StepList.Props): this {
    this.$steps.bind({
      // An h3, not a plain line: these are section subheadings on a public
      // page, and the element form of `title` exists exactly so a caller can
      // keep its own semantics. It arrives sized by the ramp and lands in the
      // stepper's shadow root, so its own type is set here.
      steps: props.steps.map((step) =>
        Stepper.plain(StepList.$title(step.title), step.description),
      ),
    });
    return this;
  }

  private static $title(text: string): HTMLElement {
    const $t = Typography.h3(text);
    $t.style.setProperty("--font-size", "var(--text-12px)");
    $t.style.setProperty("--line-height-ratio", "1.3");
    $t.style.setProperty("--margin-top", "0px");
    $t.style.setProperty("--margin-bottom", "0px");
    $t.style.fontWeight = "500";
    $t.style.color = "var(--text-pri)";
    return $t;
  }

  static $styles = [
    css`
      :host {
        display: block;
        width: 100%;
      }

      /* The marketing register, as the stepper's own inputs. Every number here
         is one the previous hand-rolled version already used; none of them is
         a position, because positions are derived. */
      #steps {
        --step-marker-size: 28px;
        --step-marker-font-size: var(--text-12px);
        --step-marker-border: none;
        --step-marker-bg: color-mix(in srgb, var(--bg) 86%, var(--text-pri) 14%);
        --step-marker-color: var(--text-pri);
        --step-marker-weight: 600;
        --step-gap: 16px;
        --step-spacing: 24px;
        --step-body-gap: 14px;
        --step-body-size: var(--text-11px);
        --step-body-measure: 52ch;
        margin-left: auto;
        margin-right: auto;
        max-width: 600px;
      }

      @media (max-width: 768px) {
        #steps {
          --step-gap: 14px;
        }
      }
    `,
  ];

  static $html = html`<std-stepper id="steps"></std-stepper>`;
}

export namespace StepList {
  export type Step = { title: string; description: string };
  export type Props = { steps: Step[] };
}
